<script setup lang="ts">
import { link } from "~/lib/utils/contents";
import { processError } from "~/lib/utils/errors";
import { defineSeo } from "~/lib/utils/seo";

const rivistaId = useParamId("rivista_id");
const numeroRivistaId = useParamId();

const omniaSdk = useOmniaSdk();

const { data: rivista, error: rivistaError } = await useAsyncData(
  `rivista-${rivistaId}`,
  async () => {
    try {
      return await omniaSdk.v1.riviste.riviste.get(rivistaId);
    } catch (error) {
      processError(error);
    }
  },
);

if (rivistaError.value) processError(rivistaError.value);

const { data, error } = await useAsyncData(
  `numero-rivista-${numeroRivistaId}`,
  async () => {
    try {
      return await omniaSdk.v1.riviste.numeri.get(rivistaId, numeroRivistaId);
    } catch (error) {
      console.log(error);
      processError(error);
    }
  },
);

if (error.value) processError(error.value);

defineSeo({
  ...data.value,
  title: data.value.title + " - " + rivista.value.title,
});
</script>

<template>
  <FrontBreadcrumHero
    :breadcrumbs="[
      { title: 'Riviste', href: '/riviste' },
      { title: rivista?.title, href: link({ rivista }) },
      { title: data?.title },
    ]"
  />

  <Skeleton v-if="!data" class="main-content w-full h-52 rounded-lg" />
  <div v-else class="main-content">
    <div class="lg:grid grid-cols-8 gap-10 xl:gap-20">
      <div class="col-start-2 col-span-6 mb-16 lg:mb-0">
        <div class="md:flex items-end justify-between mb-4">
          <div class="mb-3 md:mb-0">
            <div class="info-label">Numero Rivista</div>
          </div>
        </div>

        <!-- TITOLO -->
        <h2 class="text-4xl font-extrabold mt-5">{{ data.title }}</h2>

        <div v-if="data.capitoli" class="mb-8">
          <div
            v-for="capitolo in data.capitoli"
            :key="`capitolo-${capitolo.id}`"
            class="mt-5"
          >
            <h3 class="text-2xl font-semibold mb-3">{{ capitolo.title }}</h3>
            <p>{{ capitolo.abstract }}</p>

            <div
              class="flex flex-col gap-y-6 mt-10 mb-16 md:ms-5 ps-5 border-s-2 border-primary"
            >
              <FrontContentItem
                v-for="articolo in capitolo.articoli"
                :key="`articolo-${articolo.id}`"
                :content="articolo"
                :link-config="{}"
              />
            </div>
          </div>

          <!-- ALLEGATI -->
          <div
            v-if="data.files?.length"
            class="border rounded-sm shadow-md px-5 py-6 mb-8 print:shadow-none print:px-2 print:py-4"
          >
            <template v-for="attachment in data.files">
              <FrontDownloadButton
                v-if="attachment.file"
                :key="`attachment-${attachment.id}`"
                :content="data"
                :file="attachment"
                :title="data.title"
                class="flex items-center mb-5 last:mb-0"
              >
                <IconsAttach class="w-7 h-7 me-1" />
                <div class="link font-semibold flex gap-2">
                  <span>{{ attachment.title }}</span>
                  <span class="uppercase whitespace-nowrap"
                    >({{ attachment.file.human_readable_size }})</span
                  >
                </div>
              </FrontDownloadButton>
            </template>
          </div>
        </div>
        <div v-if="!data.allowed" class="mt-5">
          <FrontUnauthorized />
        </div>
      </div>
    </div>
  </div>

  <AdminLinkToAdmin :config="{ rivista, numeroRivista: data }" />
</template>
